import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import { FC } from 'react'

import { cn } from '@/app/utils'
import Loader from '@/components/shared/loader/ui/Loader'

import usePagination from '../hooks/usePagination'
import PaginationButtonNav from './PaginationButtonNav'
import PaginationButtonPage from './PaginationButtonPage'

type Props = {
  className?: string
  totalPages: number
  siblingCount?: number
  page: number
  onPageChange: (page: number) => void
  isFetching?: boolean
}

const AppPagination: FC<Props> = ({
  onPageChange,
  siblingCount,
  page,
  isFetching,
  totalPages,
  className
}) => {
  const paginationRange = usePagination({
    currentPage: page,
    totalPages,
    siblingCount
  })

  // If there are less than 2 times in pagination range we shall not render the component
  if (page === 0 || paginationRange.length < 2) {
    return null
  }

  const onNext = () => {
    onPageChange(page + 1)
  }

  const onPrevious = () => {
    onPageChange(page - 1)
  }

  const lastPage = paginationRange.at(-1)

  return (
    <div className={cn('flex items-center xs:justify-between', className)}>
      <PaginationButtonNav
        className="xs:order-1"
        data-testid="pagination-prev-id"
        disabled={page === 1}
        onClick={onPrevious}
      >
        <ArrowLeftIcon className="h-20 w-20" />
      </PaginationButtonNav>

      <PaginationButtonNav
        className="group ml-10 xs:order-3 xs:ml-0"
        data-testid="pagination-next-id"
        disabled={page === lastPage}
        onClick={onNext}
      >
        <ArrowRightIcon className="h-20 w-20" />
      </PaginationButtonNav>

      <ul className="ml-auto flex space-x-2 xs:order-2 xs:ml-0 xxs:pt-4">
        {isFetching ? (
          <Loader noText />
        ) : (
          paginationRange.map((pageNumber, index) => {
            return typeof pageNumber === 'number' ? (
              <li key={pageNumber}>
                <PaginationButtonPage
                  active={pageNumber === page}
                  data-testid={`pagination-page-${pageNumber}-id`}
                  onClick={() => onPageChange(pageNumber)}
                >
                  {pageNumber}
                </PaginationButtonPage>
              </li>
            ) : (
              <li
                key={`${pageNumber}-${index}`}
                className="flex h-40 w-40 items-center justify-center font-pro text-grey-70 xs:h-32 xs:w-32"
              >
                &#8230;
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}

export default AppPagination
